/**
 * BIRHAUS v3.0 Animation System
 * Smooth, delightful micro-interactions with performance-first approach
 */

export const animationsV3 = {
  // Duration scale - follows iOS guidelines for natural feel
  duration: {
    instant: '0ms',
    fast: '150ms',      // Quick feedback
    base: '300ms',      // Standard transitions
    slow: '500ms',      // Considered transitions
    slower: '700ms',    // Deliberate animations
    slowest: '1000ms',  // Hero animations
    page: '1500ms',     // Page transitions
  },
  
  // Easing curves - Natural, physics-inspired motion
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Custom BIRHAUS curves
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    snappy: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    gentle: 'cubic-bezier(0.25, 1, 0.5, 1)',
    
    // iOS-inspired curves
    entrance: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    exit: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)',
    emphasize: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    deemphasize: 'cubic-bezier(0.4, 0, 1, 1)',
  },
  
  // Scale transforms for micro-interactions
  scale: {
    none: 'scale(1)',
    subtle: 'scale(1.01)',
    small: 'scale(1.02)',
    medium: 'scale(1.05)',
    large: 'scale(1.1)',
    pressed: 'scale(0.98)',
    shrink: 'scale(0.95)',
    grow: 'scale(1.15)',
  },
  
  // Keyframe animations
  keyframes: {
    // Entrance animations
    fadeIn: {
      from: { opacity: '0' },
      to: { opacity: '1' },
    },
    
    slideUp: {
      from: { transform: 'translateY(10px)', opacity: '0' },
      to: { transform: 'translateY(0)', opacity: '1' },
    },
    
    slideDown: {
      from: { transform: 'translateY(-10px)', opacity: '0' },
      to: { transform: 'translateY(0)', opacity: '1' },
    },
    
    slideLeft: {
      from: { transform: 'translateX(10px)', opacity: '0' },
      to: { transform: 'translateX(0)', opacity: '1' },
    },
    
    slideRight: {
      from: { transform: 'translateX(-10px)', opacity: '0' },
      to: { transform: 'translateX(0)', opacity: '1' },
    },
    
    scaleIn: {
      from: { transform: 'scale(0.95)', opacity: '0' },
      to: { transform: 'scale(1)', opacity: '1' },
    },
    
    scaleOut: {
      from: { transform: 'scale(1)', opacity: '1' },
      to: { transform: 'scale(0.95)', opacity: '0' },
    },
    
    // Continuous animations
    float: {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-8px)' },
    },
    
    pulse: {
      '0%, 100%': { opacity: '1' },
      '50%': { opacity: '0.8' },
    },
    
    glow: {
      '0%, 100%': { boxShadow: '0 0 20px -5px rgba(31, 111, 235, 0.15)' },
      '50%': { boxShadow: '0 0 30px -5px rgba(31, 111, 235, 0.3)' },
    },
    
    shimmer: {
      '0%': { backgroundPosition: '-200% 0' },
      '100%': { backgroundPosition: '200% 0' },
    },
    
    // Loading animations
    spin: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    
    bounce: {
      '0%, 100%': { 
        transform: 'translateY(-25%)',
        animationTimingFunction: 'cubic-bezier(0.8, 0, 1, 1)',
      },
      '50%': { 
        transform: 'none',
        animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
      },
    },
    
    // Ripple effect for buttons
    ripple: {
      '0%': { 
        transform: 'scale(0)', 
        opacity: '1' 
      },
      '100%': { 
        transform: 'scale(20)', 
        opacity: '0' 
      },
    },
    
    // Progress animations
    indeterminate: {
      '0%': { transform: 'translateX(-100%)' },
      '100%': { transform: 'translateX(100%)' },
    },
    
    // Skeleton loading
    skeleton: {
      '0%': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
      '50%': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
      '100%': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
    },
  },
  
  // Pre-composed transition properties
  transitions: {
    all: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    colors: 'background-color 300ms, border-color 300ms, color 300ms, fill 300ms, stroke 300ms',
    opacity: 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    shadow: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    size: 'width 300ms, height 300ms',
    
    // Fast transitions for immediate feedback
    fast: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    'fast-colors': 'background-color 150ms, border-color 150ms, color 150ms',
    'fast-transform': 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Slow transitions for considered changes
    slow: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
    'slow-colors': 'background-color 500ms, border-color 500ms, color 500ms',
    'slow-transform': 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Bounce transitions for playful interactions
    bounce: 'transform 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    'bounce-scale': 'transform 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
} as const

// Component-specific animation presets
export const componentAnimations = {
  button: {
    default: animationsV3.transitions['fast-transform'],
    hover: animationsV3.scale.small,
    active: animationsV3.scale.pressed,
    focus: animationsV3.transitions.shadow,
  },
  
  card: {
    default: animationsV3.transitions.all,
    hover: `${animationsV3.transitions.transform}, ${animationsV3.transitions.shadow}`,
    float: animationsV3.keyframes.float,
  },
  
  input: {
    default: animationsV3.transitions['fast-colors'],
    focus: `${animationsV3.transitions.shadow}, ${animationsV3.transitions['fast-transform']}`,
    error: animationsV3.transitions['fast-colors'],
  },
  
  modal: {
    enter: animationsV3.keyframes.scaleIn,
    exit: animationsV3.keyframes.scaleOut,
    backdrop: animationsV3.keyframes.fadeIn,
  },
  
  dropdown: {
    enter: animationsV3.keyframes.slideDown,
    exit: animationsV3.keyframes.slideUp,
  },
  
  tooltip: {
    enter: animationsV3.keyframes.fadeIn,
    exit: animationsV3.keyframes.fadeIn, // Same as enter for consistency
  },
  
  toast: {
    enter: animationsV3.keyframes.slideRight,
    exit: animationsV3.keyframes.slideRight,
  },
  
  // Missing component animations
  activeIndicator: animationsV3.transitions['fast-transform'],
  undoBar: animationsV3.keyframes.slideUp,
  mobileMenu: animationsV3.keyframes.slideDown,
  float: animationsV3.keyframes.float,
} as const

// Animation utilities for common patterns
export const animationUtils = {
  // Stagger delays for list animations
  stagger: {
    fast: '50ms',
    base: '100ms',
    slow: '150ms',
  },
  
  // Infinite animation settings
  infinite: {
    float: `${animationsV3.keyframes.float} 3s ${animationsV3.easing.gentle} infinite`,
    pulse: `${animationsV3.keyframes.pulse} 2s ${animationsV3.easing.gentle} infinite`,
    glow: `${animationsV3.keyframes.glow} 2s ${animationsV3.easing.gentle} infinite`,
    spin: `${animationsV3.keyframes.spin} 1s ${animationsV3.easing.linear} infinite`,
  },
  
  // One-time animation presets
  oneTime: {
    fadeIn: `${animationsV3.keyframes.fadeIn} ${animationsV3.duration.base} ${animationsV3.easing.entrance}`,
    slideUp: `${animationsV3.keyframes.slideUp} ${animationsV3.duration.base} ${animationsV3.easing.entrance}`,
    scaleIn: `${animationsV3.keyframes.scaleIn} ${animationsV3.duration.base} ${animationsV3.easing.bounce}`,
    ripple: `${animationsV3.keyframes.ripple} 600ms ${animationsV3.easing.easeOut}`,
  },
} as const

// Performance-optimized animation classes
export const performantAnimations = {
  // GPU-accelerated transforms
  gpu: 'transform-gpu will-change-transform',
  'gpu-opacity': 'transform-gpu will-change-transform will-change-opacity',
  'gpu-auto': 'transform-gpu will-change-auto',
  
  // Reduce motion preferences
  'reduce-motion': 'motion-reduce:transition-none motion-reduce:animate-none',
  
  // Prefers reduced motion fallbacks
  accessible: 'motion-reduce:transition-none motion-reduce:transform-none',
} as const